"use client";
import Image from "next/image";
import React from "react";
import { toast } from "sonner";
import axiosInstance, { endpoints } from "@/lib/axios";

interface CareerGuidanceActualitesArticleProps {
    /**
     * Category (news type) name injected in the heading, e.g. "MARKETING".
     * Omitted on pages with no category context — the heading then drops the
     * "en <thematique>" segment entirely.
     */
    thematique?: string;
}

const CareerGuidanceActualitesArticle: React.FC<CareerGuidanceActualitesArticleProps> = ({ thematique }) => {
    const [formData, setFormData] = React.useState({
        entreprise: '',
        prenom: '',
        nom: '',
        email: '',
        phone: '',
    });
    const [submitting, setSubmitting] = React.useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // "entreprise" est facultatif : il ne bloque pas l'envoi du formulaire.
    const isFormValid = ([formData.prenom, formData.nom, formData.email, formData.phone])
        .every(value => value.trim() !== '');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isFormValid || submitting) return;
        setSubmitting(true);
        try {
            await axiosInstance.post(endpoints.leads.create, {
                first_name: formData.prenom,
                last_name: formData.nom,
                email: formData.email,
                phone: `+33${formData.phone}`,
                message: [
                    "Demande de création de dossier de candidature.",
                    formData.entreprise.trim() && `Entreprise : ${formData.entreprise.trim()}`,
                ]
                    .filter(Boolean)
                    .join("\n"),
                origin: "form",
            });
            toast.success("Demande envoyée avec succès.");
            setFormData({ entreprise: '', prenom: '', nom: '', email: '', phone: '' });
        } catch {
            toast.error("Une erreur est survenue lors de l'envoi. Veuillez réessayer.");
        } finally {
            setSubmitting(false);
        }
    };


    return (
        <section className="w-full bg-[#EFEFEF]/30 text-black pt-12.5 pb-14.25 px-4 relative">
            <div className="flex items-center bg-white rounded-[40px] border-b border-[#40BFDE] py-3 px-4 justify-center absolute left-1/2 -translate-x-1/2 -top-5">
                <Image src='/assets/small-logo.svg' alt='small-logo' title='small-logo' width={30} height={17} />
            </div>

            <h2 className="font-red-hat text-[#1B365E] text-[30px] md:text-[40px] leading-10 md:leading-13.25 font-light max-w-190 mx-auto text-center">
                {thematique
                    ? `Quelle formation en ${thematique} est faite pour vous ?`
                    : 'Quelle formation est faite pour vous ?'}
            </h2>

            <div className="mt-[68.5px] max-w-258.25 w-full mx-auto flex md:flex-row flex-col items-start text-left justify-between gap-8 md:gap-6">
                <div className="max-w-116.75 w-full md:text-left text-center">
                    <h3 className="text-[#1B365E] text-[15px] md:text-[20px] leading-4.5 md:leading-7.5 tracking-[0.01rem] font-bold md:font-normal">Faites le test : deux minutes suffisent pour <span className="font-bold">construire le parcours de formation</span> qui vous correspond.</h3>
                    <ul className="mt-8 md:mt-11 flex items-start justify-center flex-col mx-auto w-full space-y-5.75">
                        <li className="flex items-center gap-3.25">
                            <div className="sm:w-5.25 h-4 w-4 sm:h-5.25 rounded-full bg-[#40BFDE]"></div>
                            <p className="text-[#1B365E] text-[12px] sm:text-[16px] leading-3.5 sm:leading-6.5 tracking-[0.01rem] font-bold">Évaluer vos objectifs d&apos;apprentissage</p>
                        </li>
                        <li className="flex items-center gap-3.25">
                            <div className="sm:w-5.25 h-4 w-4 sm:h-5.25 rounded-full bg-[#40BFDE]"></div>
                            <p className="text-[#1B365E] text-[12px] sm:text-[16px] leading-3.5 sm:leading-6.5 tracking-[0.01rem] font-bold">Comprendre vos preferences</p>
                        </li>
                        <li className="flex items-center gap-3.25">
                            <div className="sm:w-5.25 h-4 w-4 sm:h-5.25 rounded-full bg-[#40BFDE]"></div>
                            <p className="text-[#1B365E] text-[12px] sm:text-[16px] leading-3.5 sm:leading-6.5 tracking-[0.01rem] font-bold">Choisir le bon format de formation</p>
                        </li>
                        <li className="flex items-center gap-3.25">
                            <div className="sm:w-5.25 h-4 w-4 sm:h-5.25 rounded-full bg-[#40BFDE]"></div>
                            <p className="text-[#1B365E] text-[12px] sm:text-[16px] leading-3.5 sm:leading-6.5 tracking-[0.01rem] font-bold">Construire votre parcours de formation</p>
                        </li>
                    </ul>
                </div>

                <div className="max-w-116.75 w-full">
                    {/* <h3 className="text-[#1B365E] text-[15px] sm:text-[20px] leading-4.5 sm:leading-7.5 font-bold sm:font-medium tracking-[0.4px]">Démarrez votre nouvelle trajectoire :</h3> */}
                    <form onSubmit={handleSubmit} className="mt-4.5 sm:mt-5">
                        <div className="mb-4 md:mb-7.25">
                            <input
                                type="text"
                                name="entreprise"
                                placeholder="Entreprise (facultatif)"
                                value={formData.entreprise}
                                onChange={handleChange}
                                className="w-full h-10 px-3 rounded-lg border border-[#EFEFEF] bg-white placeholder:text-[#ABABAB] text-[12px] font-normal text-[#1B365E] focus:outline-none focus:border-[#40BFDE] focus:ring-1 focus:ring-[#40BFDE]"
                            />
                        </div>
                        <div className="mb-4 md:mb-7.25">
                            <input
                                type="text"
                                name="prenom"
                                placeholder="Prénom"
                                value={formData.prenom}
                                onChange={handleChange}
                                className="w-full h-10 px-3 rounded-lg border border-[#EFEFEF] bg-white placeholder:text-[#ABABAB] text-[12px] font-normal text-[#1B365E] focus:outline-none focus:border-[#40BFDE] focus:ring-1 focus:ring-[#40BFDE]"
                            />
                        </div>
                        <div className="mb-4 md:mb-7.25">
                            <input
                                type="text"
                                name="nom"
                                placeholder="Nom"
                                value={formData.nom}
                                onChange={handleChange}
                                className="w-full h-10 px-3 rounded-lg border border-[#EFEFEF] bg-white placeholder:text-[#ABABAB] text-[12px] font-normal text-[#1B365E] focus:outline-none focus:border-[#40BFDE] focus:ring-1 focus:ring-[#40BFDE]"
                            />
                        </div>
                        <div className="mb-4 md:mb-7.25">
                            <input
                                type="email"
                                name="email"
                                placeholder="Email"
                                value={formData.email}
                                onChange={handleChange}
                                className="w-full h-10 px-3 rounded-lg border border-[#EFEFEF] bg-white placeholder:text-[#ABABAB] text-[12px] font-normal text-[#1B365E] focus:outline-none focus:border-[#40BFDE] focus:ring-1 focus:ring-[#40BFDE]"
                            />
                        </div>
                        <div className="flex gap-2.5 sm:gap-4.5">
                            <div className="w-14 h-10 px-3 flex items-center justify-center rounded-lg border border-[#EFEFEF] bg-white text-[12px] font-normal text-[#ABABAB]">
                                +33
                            </div>
                            <input
                                type="tel"
                                name="phone"
                                placeholder="Portable"
                                value={formData.phone}
                                onChange={handleChange}
                                className="w-full h-10 px-3 rounded-lg border border-[#EFEFEF] bg-white placeholder:text-[#ABABAB] text-[12px] font-normal text-[#1B365E] focus:outline-none focus:border-[#40BFDE] focus:ring-1 focus:ring-[#40BFDE]"
                            />
                        </div>

                        <div className="text-[12px] text-[#1B365E] leading-5 mb-5 mt-6.25 font-medium tracking-[0.25px]">
                            *Tous les champs sont obligatoires, sauf « Entreprise »
                        </div>

                        <button
                            type="submit"
                            className={`w-full h-12 sm:h-14.5 border font-bold text-[16px] rounded-lg transition-all duration-300 ${isFormValid && !submitting
                                ? "bg-[#1B365E] border-[#1B365E] text-white hover:bg-[#152b4d] cursor-pointer"
                                : "bg-[#D9D9D9] border-[#ABABAB] text-[#ABABAB] cursor-not-allowed"
                                }`}
                            disabled={!isFormValid || submitting}
                        >
                            {submitting ? "Envoi en cours..." : "Envoyer ma demande"}
                        </button>
                    </form>
                </div>
            </div>

        </section>
    );
}


export default CareerGuidanceActualitesArticle